import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowRight, Loader2, ShieldCheck, AlertCircle } from "lucide-react";

const SetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [validating, setValidating] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/auth/password-setup/${token}`);
        let data = {};
        try {
          const text = await res.text();
          data = text ? JSON.parse(text) : {};
        } catch {
          data = {};
        }
        if (!res.ok) {
          throw new Error(data?.detail || "Link invalid or already used. Ask an admin for a new setup link.");
        }
        setInfo(data);
      } catch (err) {
        const m = err?.message || "";
        const friendly =
          /body stream|Failed to fetch|NetworkError/i.test(m)
            ? "Link invalid or already used. Ask an admin for a new setup link."
            : m;
        setError(friendly);
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/password-setup/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = {}; }
      if (!res.ok) throw new Error(data?.detail || "Could not set password");
      setUser(data);
      toast.success("Password set — you're logged in!");
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="set-password-page">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md border border-slate-200 shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-[#FFD507]/20 mx-auto flex items-center justify-center mb-3">
                <ShieldCheck className="w-7 h-7 text-[#1E3A8A]" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">Set up your login</h1>
              <p className="text-sm text-slate-500 mt-1">Choose a password to finish creating your Aspiritech Academy account.</p>
            </div>

            {validating ? (
              <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
            ) : error && !info ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="setup-error">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">{error}</p>
                  <p className="text-sm text-red-700 mt-1">Ask an admin to send you a new setup link.</p>
                  <Link to="/login" className="inline-flex items-center mt-2 text-sm text-[#1E3A8A] hover:underline">Go to login →</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {info && (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <p className="text-slate-600 text-xs">Setting password for</p>
                    <p className="font-semibold text-slate-900" data-testid="setup-email">{info.email}</p>
                    {info.name && <p className="text-xs text-slate-500">{info.name}</p>}
                  </div>
                )}
                {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
                <div>
                  <Label htmlFor="pw">Choose a password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="pl-10" minLength={6} required data-testid="setup-pw" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">At least 6 characters.</p>
                </div>
                <div>
                  <Label htmlFor="pw2">Confirm password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="pl-10" minLength={6} required data-testid="setup-pw2" />
                  </div>
                </div>
                <Button type="submit" disabled={submitting || pw.length < 6} className="w-full btn-primary h-11" data-testid="setup-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Set password &amp; sign in <ArrowRight className="w-4 h-4 ml-1" /></>)}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default SetPasswordPage;
